// ActivityFourWordCloud.jsx — Safe activity (Phase 3: real logic).
// Matches room.php's #myModal4 word-cloud behavior exactly: the SVG is
// loaded and injected inline (not a static <img>) so individual words can be
// clicked; clicking a word colors every element sharing its fill class
// green (the decorative repeats light up together, same as room.php's
// `$(className).css({fill:'green'})`); each *unique* word scores once;
// Submit only appears once >= 3 unique words are found (confirmed from
// room.php's `toggleSubmitButton()`), max 6.
import React, { useEffect, useRef, useState } from 'react';
import ActivityPopup from '../overlays/ActivityPopup';
import { SAFE_TITLE } from '../../../games/escape-to-inner-peace/data/gameText';
import { SAFE_WORDS, SAFE_MIN_WORDS_FOR_SUBMIT } from '../../../games/escape-to-inner-peace/data/wordCloudData';
import { loadWordCloudSvg } from '../../../games/escape-to-inner-peace/utils/loadWordCloudSvg';
import '../../../games/escape-to-inner-peace/styles/theme.css';
import './ActivityFourWordCloud.css';

const WORD_BY_ID = Object.fromEntries(SAFE_WORDS.map((w) => [w.id, w]));

function ActivityFourWordCloud({ open, onComplete }) {
  const wrapperRef = useRef(null);
  const [markup, setMarkup] = useState(null);
  const [found, setFound] = useState(() => new Set());
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    loadWordCloudSvg().then((svg) => {
      if (!cancelled) setMarkup(svg);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // The SVG word groups are made of individual letter paths with gaps
  // between them (e.g. the space inside "Hydrate" between h and y) — those
  // gaps have no fill, so clicks there hit nothing.
  //
  // A first attempt filled each word's whole getBBox() with an invisible
  // rect, but that bbox turned out to be far larger than the word's visible
  // glyph cluster (this font's path data uses much larger local units than
  // the tightly-cropped visual instance suggests) — verified directly: a
  // 121-point grid sampled across "Hydrate"'s own screen area found zero
  // points where elementFromPoint actually resolved to Hydrate, because a
  // different, later-painted word's equally oversized rect covered the
  // entire region on top of it. That's a regression, not a fix, in this
  // densely-overlapping artwork.
  //
  // Instead: give each letter path a small invisible `stroke`. A stroke
  // only extends a path's own hit-testable area by a fixed margin around
  // its *actual* ink — proportional and local to that glyph, never a giant
  // shared rectangle — so it closes small inter-letter gaps within one
  // word without swallowing clicks meant for a different, overlapping word.
  useEffect(() => {
    if (!markup || !wrapperRef.current) return;
    const svgEl = wrapperRef.current.querySelector('svg');
    if (!svgEl) return;
    const LOCAL_STROKE_WIDTH = 10; // SVG user units, scales with each word's own transform
    SAFE_WORDS.forEach((word) => {
      const group = svgEl.getElementById(word.id);
      if (!group) return;
      group.querySelectorAll('path').forEach((path) => {
        path.style.stroke = 'transparent';
        path.style.strokeWidth = `${LOCAL_STROKE_WIDTH}`;
        path.style.strokeLinejoin = 'round';
      });
    });
  }, [markup]);

  const handleWrapperClick = (event) => {
    if (submitted) return;
    const target = event.target.closest('[id]');
    const word = target && WORD_BY_ID[target.id];
    if (!word || found.has(word.id)) return;

    setFound((prev) => {
      const next = new Set(prev);
      next.add(word.id);
      return next;
    });

    const matches = wrapperRef.current?.querySelectorAll(`.${word.cssClass}`) || [];
    matches.forEach((el) => {
      el.style.fill = 'green';
    });
  };

  const handleSubmit = () => {
    if (submitted || found.size < SAFE_MIN_WORDS_FOR_SUBMIT) return;
    setSubmitted(true);
    setTimeout(() => {
      onComplete(found.size);
    }, 1000);
  };

  const submitVisible = found.size >= SAFE_MIN_WORDS_FOR_SUBMIT;

  return (
    <ActivityPopup
      open={open}
      title={SAFE_TITLE}
      plainHeader
      boldTitle={false}
      testId="escape-activity-safe"
      footer={
        submitVisible ? (
          <button
            type="button"
            className="escape-submit-btn"
            disabled={submitted}
            aria-label="Submit"
            data-testid="escape-safe-submit"
            onClick={handleSubmit}
          />
        ) : null
      }
    >
      <div
        className="escape-wordcloud-wrap"
        ref={wrapperRef}
        onClick={handleWrapperClick}
        data-testid="escape-wordcloud-wrap"
      >
        {markup ? (
          // eslint-disable-next-line react/no-danger
          <div dangerouslySetInnerHTML={{ __html: markup }} />
        ) : (
          <div className="escape-wordcloud-loading">Loading…</div>
        )}
      </div>
    </ActivityPopup>
  );
}

export default ActivityFourWordCloud;
